export type IntelligenceProgressStatus =
  | 'planning'
  | 'awaiting_confirmation'
  | 'pending'
  | 'queued'
  | 'running'
  | 'in_progress'
  | 'interrupted'
  | 'podcast_pending'
  | 'completed'
  | 'failed'
  | 'cancelled'

export type ProgressDisplayPhase =
  | 'collecting'
  | 'analyzing'
  | 'synthesizing'
  | 'structuring'
  | 'generating'
  | 'finalizing'

export type GetIntelligenceProgressStateInput = {
  status?: IntelligenceProgressStatus | string
  rawPhase?: string
  createdAt?: string | Date
  nowMs?: number
}

export type GetIntelligenceProgressLabelInput = GetIntelligenceProgressStateInput & {
  stepLabel?: string
}

export type IntelligenceProgressState = {
  progress: number
  displayPhase: string
}

export type IntelligenceProgressPhaseConfig = {
  id: ProgressDisplayPhase
  label: string
  description: string
  progressRange: [number, number]
  phaseNumber: number
}

export const INTELLIGENCE_PROGRESS_PHASES: IntelligenceProgressPhaseConfig[] = [
  {
    id: 'collecting',
    label: 'Collecting Sources',
    description: 'Gathering information from trusted sources',
    progressRange: [0, 25],
    phaseNumber: 1,
  },
  {
    id: 'analyzing',
    label: 'Analyzing Intelligence',
    description: 'Processing and extracting key insights',
    progressRange: [25, 50],
    phaseNumber: 2,
  },
  {
    id: 'synthesizing',
    label: 'Synthesizing Insights',
    description: 'Connecting patterns and implications',
    progressRange: [50, 70],
    phaseNumber: 3,
  },
  {
    id: 'structuring',
    label: 'Structuring Report',
    description: 'Organizing findings into coherent sections',
    progressRange: [70, 85],
    phaseNumber: 4,
  },
  {
    id: 'generating',
    label: 'Writing Report',
    description: 'Generating your comprehensive analysis',
    progressRange: [85, 95],
    phaseNumber: 5,
  },
  {
    id: 'finalizing',
    label: 'Finalizing',
    description: 'Adding finishing touches',
    progressRange: [95, 100],
    phaseNumber: 6,
  },
]

const QUERY_ANALYSIS_PROGRESS = 12

const BACKEND_PROGRESS: Record<string, number> = {
  planning: 5,
  initializing: 3,
  plan: 5,
  analyze: QUERY_ANALYSIS_PROGRESS,
  query_analysis: QUERY_ANALYSIS_PROGRESS,
  collect: 20,
  deep_research: 30,
  extract: 45,
  evidence_audit: 55,
  scenario: 65,
  sib: 75,
  write: 85,
  qa: 92,
  refine: 96,
  finalizing: 97,
  podcast: 97,
  finalized: 100,
  completed: 100,
}

const BACKEND_PHASE_MAP: Record<string, ProgressDisplayPhase> = {
  plan: 'collecting',
  analyze: 'collecting',
  query_analysis: 'collecting',
  collect: 'collecting',
  deep_research: 'collecting',
  extract: 'analyzing',
  evidence_audit: 'analyzing',
  scenario: 'synthesizing',
  sib: 'structuring',
  write: 'generating',
  qa: 'finalizing',
  refine: 'finalizing',
  finalized: 'finalizing',
  completed: 'finalizing',
}

const DISPLAY_PHASE_LABELS: Record<ProgressDisplayPhase, string> = {
  collecting: 'Gathering sources...',
  analyzing: 'Analyzing intelligence...',
  synthesizing: 'Connecting insights...',
  structuring: 'Structuring report...',
  generating: 'Writing final report...',
  finalizing: 'Almost ready!',
}

const RAW_PHASE_LABELS: Record<string, string> = {
  initializing: 'Initializing...',
  starting: 'Starting...',
  planning: 'Designing research plan...',
  awaiting_confirmation: 'Ready for your review',
  analyze: 'Analyzing query requirements...',
  running: 'Starting...',
  in_progress: 'Starting...',
  plan: 'Creating research plan...',
  collect: 'Collecting sources...',
  deep_research: 'Deep research...',
  extract: 'Extracting evidence...',
  evidence_audit: 'Auditing evidence...',
  scenario: 'Building scenarios...',
  sib: 'Compiling intelligence brief...',
  write: 'Writing report...',
  qa: 'Quality assurance...',
  refine: 'Refining report...',
  finalizing: 'Almost ready!',
  podcast: 'Preparing audio...',
  finalized: 'Complete!',
  completed: 'Complete!',
}

function parseCreatedAtMs(createdAt?: string | Date): number | null {
  if (!createdAt) return null

  if (createdAt instanceof Date) {
    const createdAtMs = createdAt.getTime()
    return Number.isNaN(createdAtMs) ? null : createdAtMs
  }

  const normalizedCreatedAt = /(?:[zZ]|[+-]\d{2}:\d{2})$/.test(createdAt)
    ? createdAt
    : `${createdAt}Z`

  const createdAtMs = Date.parse(normalizedCreatedAt)
  return Number.isNaN(createdAtMs) ? null : createdAtMs
}

function normalizePhaseKey(value?: string): string | null {
  const normalized = value?.trim().toLowerCase()
  return normalized ? normalized : null
}

function isQueryAnalysisPhase(value?: string): boolean {
  const lower = normalizePhaseKey(value)
  if (!lower) return false

  return (
    lower === 'analyze' ||
    lower === 'analysis' ||
    lower === 'query_analysis' ||
    lower === 'query-analysis' ||
    lower === 'query analysis' ||
    (
      (lower.includes('analyz') || lower.includes('analysis') || lower.includes('process')) &&
      (lower.includes('query') || lower.includes('requirement'))
    )
  )
}

function getPhaseFromProgress(progress: number): ProgressDisplayPhase {
  if (progress < 25) return 'collecting'
  if (progress < 50) return 'analyzing'
  if (progress < 70) return 'synthesizing'
  if (progress < 85) return 'structuring'
  if (progress < 95) return 'generating'
  return 'finalizing'
}

function getTimeBasedStartupProgress(elapsedSeconds: number): number {
  if (elapsedSeconds < 60) {
    return Math.min(8, 2 + Math.floor(elapsedSeconds / 10))
  }

  const elapsedMinutes = elapsedSeconds / 60
  return 5 + Math.min(90, Math.floor((elapsedMinutes / 30) * 90))
}

function getBasePhaseProgress(status?: string, rawPhase?: string): number {
  if (!rawPhase) {
    if (status === 'podcast_pending') return 97
    if (status === 'awaiting_confirmation') return 100

    if (
      status === 'planning' ||
      status === 'pending' ||
      status === 'queued' ||
      status === 'running' ||
      status === 'in_progress' ||
      status === 'interrupted'
    ) {
      return 5
    }

    return 2
  }

  const lower = rawPhase.toLowerCase()

  if (BACKEND_PROGRESS[lower] !== undefined) {
    return BACKEND_PROGRESS[lower]
  }

  if (isQueryAnalysisPhase(lower)) return QUERY_ANALYSIS_PROGRESS
  if (lower.includes('plan') || lower.includes('init')) return 5
  if (lower.includes('collect') || lower.includes('gather') || lower.includes('deep')) return 20
  if (lower.includes('extract') || lower.includes('evidence') || lower.includes('analyz') || lower.includes('process')) return 45
  if (lower.includes('scenario') || lower.includes('synth')) return 65
  if (lower.includes('sib') || lower.includes('brief') || lower.includes('struct')) return 75
  if (lower.includes('writ') || lower.includes('generat')) return 85
  if (lower.includes('qa') || lower.includes('quality')) return 92
  if (lower.includes('final') || lower.includes('refine')) return 96

  return 5
}

export function matchesIntelligenceProgressPhase(
  currentPhase: string | undefined,
  phaseId: ProgressDisplayPhase
): boolean {
  if (!currentPhase) return false

  const lower = currentPhase.toLowerCase()
  const mappedPhase = BACKEND_PHASE_MAP[lower]
  if (mappedPhase === phaseId) return true

  switch (phaseId) {
    case 'collecting':
      return isQueryAnalysisPhase(lower) || lower.includes('collect') || lower.includes('gather') || lower.includes('deep')
    case 'analyzing':
      return !isQueryAnalysisPhase(lower) && (
        lower.includes('analyz') || lower.includes('process') || lower.includes('extract') || lower.includes('evidence')
      )
    case 'synthesizing':
      return lower.includes('synth') || lower.includes('scenario') || lower.includes('connect') || lower.includes('pattern')
    case 'structuring':
      return lower.includes('struct') || lower.includes('organ') || lower.includes('sib') || lower.includes('brief')
    case 'generating':
      return lower.includes('writ') || lower.includes('generat') || lower.includes('draft')
    case 'finalizing':
      return lower.includes('final') || lower.includes('finish') || lower.includes('complete') || lower.includes('qa') || lower.includes('refine')
  }
}

export function getIntelligenceProgressState(
  input: GetIntelligenceProgressStateInput
): IntelligenceProgressState {
  const { status, rawPhase, createdAt, nowMs = Date.now() } = input

  if (status === 'podcast_pending') {
    return {
      progress: 97,
      displayPhase: 'finalizing',
    }
  }

  if (status === 'awaiting_confirmation') {
    return {
      progress: 100,
      displayPhase: 'finalizing',
    }
  }

  const basePhaseProgress = getBasePhaseProgress(status, rawPhase)
  const createdAtMs = parseCreatedAtMs(createdAt)

  if (createdAtMs !== null) {
    const elapsedSeconds = Math.max(0, Math.floor((nowMs - createdAtMs) / 1000))

    if (elapsedSeconds < 180) {
      if (rawPhase && basePhaseProgress > 8) {
        const blendFactor = Math.max(0, (elapsedSeconds - 60) / 120)
        const blendedProgress = elapsedSeconds < 60
          ? getTimeBasedStartupProgress(elapsedSeconds)
          : Math.round(8 + (basePhaseProgress - 8) * blendFactor)

        return {
          progress: blendedProgress,
          displayPhase: getPhaseFromProgress(blendedProgress),
        }
      }

      const startupProgress = getTimeBasedStartupProgress(elapsedSeconds)
      return {
        progress: startupProgress,
        displayPhase: getPhaseFromProgress(startupProgress),
      }
    }
  }

  return {
    progress: basePhaseProgress,
    displayPhase: rawPhase || getPhaseFromProgress(basePhaseProgress),
  }
}

export function getIntelligenceProgressLabel(
  input: GetIntelligenceProgressLabelInput
): string {
  const state = getIntelligenceProgressState(input)
  const normalizedStatus = normalizePhaseKey(input.status)
  const normalizedDisplayPhase = normalizePhaseKey(state.displayPhase)
  const normalizedRawPhase = normalizePhaseKey(input.rawPhase)
  const stepLabel = input.stepLabel?.trim()

  if (normalizedStatus === 'planning' || normalizedStatus === 'awaiting_confirmation') {
    return RAW_PHASE_LABELS[normalizedStatus] ?? 'Starting...'
  }

  if (normalizedDisplayPhase && DISPLAY_PHASE_LABELS[normalizedDisplayPhase as ProgressDisplayPhase]) {
    return DISPLAY_PHASE_LABELS[normalizedDisplayPhase as ProgressDisplayPhase]
  }

  return (
    stepLabel ||
    (normalizedRawPhase ? RAW_PHASE_LABELS[normalizedRawPhase] : null) ||
    (normalizedStatus ? RAW_PHASE_LABELS[normalizedStatus] : null) ||
    'Starting...'
  )
}

export function getIntelligenceProgressPhase(input: GetIntelligenceProgressStateInput) {
  const state = getIntelligenceProgressState(input)
  return (
    INTELLIGENCE_PROGRESS_PHASES.find(phase =>
      matchesIntelligenceProgressPhase(state.displayPhase, phase.id)
    ) ?? INTELLIGENCE_PROGRESS_PHASES[0]
  )
}

export function formatIntelligenceEta(seconds: number): string {
  if (seconds < 60) return 'Less than a minute'

  const minutes = Math.ceil(seconds / 60)
  if (minutes === 1) return 'About 1 minute'
  if (minutes < 60) return `About ${minutes} minutes`

  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  if (hours === 1 && remainingMinutes === 0) return 'About 1 hour'
  if (remainingMinutes === 0) return `About ${hours} hours`
  return `About ${hours}h ${remainingMinutes}m`
}
